import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkModeRounded";
import LightModeIcon from "@mui/icons-material/LightModeRounded";
import { MotionConfig, AnimatePresence, motion } from "framer-motion";
import { useTheme } from "../../providers/ThemeContext";
import { useLocation } from "react-router-dom";

const MotionIconButton = motion(IconButton);
const MotionBox = motion(Box);

const AnimatedThemeToggle = ({ color }: { color?: string }) => {
  const { isDark, toggleTheme, theme } = useTheme();
  const { pathname } = useLocation();

  // Home page lives under the /:lang prefix ("/en" or "/ar"), i.e. the path has
  // only the language segment. Force the icon white on home (contrast against
  // the hero) and white everywhere in dark mode; black on other pages in light.
  const isHome = pathname.split("/").filter(Boolean).length <= 1;
  const iconColor = color ?? (isHome || isDark ? "#fff" : "#000");

  return (
    <MotionConfig reducedMotion="user">
      <Tooltip
        title={isDark ? "Switch to Light" : "Switch to Dark"}
        arrow
        enterDelay={300}
      >
        <MotionBox
          onClick={toggleTheme}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.96, rotate: isDark ? -6 : 6 }}
          transition={{ type: "spring", stiffness: 400, damping: 20 }}
          sx={{
            position: "relative",
            borderRadius: 0,
            p: 0,
          }}
        >
          {/* subtle animated halo (kept as-is; optional to force white too) */}
          <MotionBox
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.45 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            sx={{
              position: "absolute",
              inset: -6,
              borderRadius: "inherit",
              filter: "blur(12px)",
              background: `radial-gradient(60% 60% at 50% 50%, ${
                isHome ? "#ffffff55" : `${theme.primary}55`
              }, transparent 70%)`,
              pointerEvents: "none",
            }}
          />

          <MotionIconButton
            disableRipple
            size="small"
            sx={{
              width: "auto",
              height: "auto",
              borderRadius: 0,
              color: iconColor, // ← key line
              backgroundColor: "transparent",
              boxShadow: "none",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {isDark ? (
                <motion.span
                  key="moon"
                  initial={{ y: 10, opacity: 0, rotate: -15 }}
                  animate={{ y: 0, opacity: 1, rotate: 0 }}
                  exit={{ y: -10, opacity: 0, rotate: 15 }}
                  transition={{ duration: 0.24 }}
                  style={{ display: "inline-flex" }}
                >
                  <DarkModeIcon fontSize="small" />
                </motion.span>
              ) : (
                <motion.span
                  key="sun"
                  initial={{ y: 10, opacity: 0, rotate: 15 }}
                  animate={{ y: 0, opacity: 1, rotate: 0 }}
                  exit={{ y: -10, opacity: 0, rotate: -15 }}
                  transition={{ duration: 0.24 }}
                  style={{ display: "inline-flex" }}
                >
                  <LightModeIcon fontSize="small" />
                </motion.span>
              )}
            </AnimatePresence>
          </MotionIconButton>
        </MotionBox>
      </Tooltip>
    </MotionConfig>
  );
};

export default AnimatedThemeToggle;
